/**
 * Firm Wallet — passbook + balance + recharge CTA.
 *
 * Shows running balance, low-balance alert, latest credits & debits with the
 * tier/model attribution per row. Phase 8: filter by provider/user/month +
 * 12-month spend trend chart. Recharge button sends owners to Razorpay top-up.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { Wallet, ArrowDownLeft, ArrowUpRight, Loader2, AlertCircle, Plus, Filter, TrendingUp } from "lucide-react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { fmtDateTime } from "@/lib/time";
import { useAccess } from "@/context/AccessContext";
import { useTableControls, SortableTh, FilterChip } from "@/components/SortableTable";

const DEFAULT_PRESETS = [500, 1000, 2500, 5000];
const DEFAULT_MIN = 100;

function loadRazorpayScript() {
    return new Promise((resolve) => {
        if (window.Razorpay) return resolve(true);
        const s = document.createElement("script");
        s.src = "https://checkout.razorpay.com/v1/checkout.js";
        s.onload = () => resolve(true);
        s.onerror = () => resolve(false);
        document.body.appendChild(s);
    });
}

export default function WalletPage() {
    const { can } = useAccess();
    const canTopup = can("wallet", "edit");
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [walletCfg, setWalletCfg] = useState({ presets: DEFAULT_PRESETS, min_topup: DEFAULT_MIN, max_topup: null, currency: "INR" });
    const [amount, setAmount] = useState(DEFAULT_PRESETS[1]);
    const [busy, setBusy] = useState(false);
    const [filters, setFilters] = useState({ provider: "", user_id: "", month: "" });
    const [bonusQuote, setBonusQuote] = useState(null);

    // Live founder-bonus quote — debounced as the amount changes so the
    // recharge UI can show "you'll actually get ₹X" before opening Razorpay.
    useEffect(() => {
        if (!amount || amount <= 0) { setBonusQuote(null); return; }
        const t = setTimeout(() => {
            api.get(`/wallet/bonus-quote?amount=${amount}`)
                .then(r => setBonusQuote(r.data))
                .catch(() => setBonusQuote(null));
        }, 250);
        return () => clearTimeout(t);
    }, [amount]);

    const reload = useCallback(async () => {
        try {
            const params = new URLSearchParams({ limit: "200" });
            if (filters.provider) params.set("provider", filters.provider);
            if (filters.user_id)  params.set("user_id", filters.user_id);
            if (filters.month)    params.set("month", filters.month);
            const r = await api.get(`/wallet/passbook?${params.toString()}`);
            setData(r.data);
        } catch (e) { toast.error(apiError(e)); }
    }, [filters]);
    useEffect(() => { reload().finally(() => setLoading(false)); }, [reload]);  // eslint-disable-line

    // Load super-admin-configured min / presets once
    useEffect(() => {
        api.get("/platform/wallet-config").then(r => {
            const cfg = r.data || {};
            const presets = (cfg.presets && cfg.presets.length) ? cfg.presets : DEFAULT_PRESETS;
            setWalletCfg({ ...cfg, presets });
            setAmount(presets[Math.min(1, presets.length - 1)] || cfg.min_topup || DEFAULT_MIN);
        }).catch(() => { /* fall back to defaults */ });
    }, []);

    const recharge = async () => {
        const min = walletCfg.min_topup || DEFAULT_MIN;
        const max = walletCfg.max_topup;
        if (!amount || amount < min) { toast.error(`Minimum top-up is ₹${min}`); return; }
        if (max && amount > max) { toast.error(`Maximum top-up is ₹${max}`); return; }
        setBusy(true);
        try {
            const ok = await loadRazorpayScript();
            if (!ok) { toast.error("Could not load Razorpay. Check your network."); setBusy(false); return; }
            const { data: order } = await api.post("/wallet/topup/start", { amount });
            const rzp = new window.Razorpay({
                key: order.key_id, amount: order.amount, currency: order.currency,
                order_id: order.order_id, name: "Law OS — Wallet top-up",
                description: `Add ₹${amount} to your AI wallet`,
                theme: { color: "#D4AF37" },
                handler: async (resp) => {
                    try {
                        const verify = await api.post("/wallet/topup/verify", resp);
                        const credited = verify?.data?.credited ?? amount;
                        const bonus = verify?.data?.bonus_amount ?? 0;
                        if (bonus > 0) {
                            toast.success(`₹${credited} credited (₹${amount} + ₹${bonus} founder bonus)`);
                        } else {
                            toast.success(`₹${amount} credited to wallet`);
                        }
                        reload();
                    } catch (e) { toast.error(apiError(e)); }
                },
                modal: { ondismiss: () => setBusy(false) },
            });
            rzp.open();
        } catch (e) { toast.error(apiError(e)); setBusy(false); return; }
    };

    const clearFilters = () => setFilters({ provider: "", user_id: "", month: "" });
    const anyFilter = !!(filters.provider || filters.user_id || filters.month);

    const monthOptions = useMemo(() => {
        const out = [];
        const now = new Date();
        for (let i = 0; i < 12; i++) {
            const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
            const k = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
            out.push({ value: k, label: d.toLocaleDateString("en-IN", { month: "long", year: "numeric", timeZone: "Asia/Kolkata" }) });
        }
        return out;
    }, []);

    const passbookCtrl = useTableControls(data?.transactions || [], {
        storageKey: "wallet-passbook",
        accessors: {
            provider: (t) => t.provider_label || t.provider,
            by: (t) => t.user_name || t.user_email || "",
            amount: (t) => Number(t.amount) * (t.kind === "credit" ? 1 : -1),
            activity: (t) => t.note || t.ref || "",
        },
    });

    if (loading) return <div className="text-center text-muted-foreground text-sm py-12"><Loader2 className="w-4 h-4 inline animate-spin mr-2"/>Loading…</div>;
    if (!data) return null;
    const balance = data.balance || 0;
    const threshold = data.low_balance_threshold || 0;
    const isLow = balance <= threshold;
    const isEmpty = balance <= 0;

    return (
        <div className="space-y-6" data-testid="wallet-page">
            <div>
                <div className="overline">AI Spend</div>
                <h1 className="display-display mt-1 inline-flex items-center gap-3">
                    <Wallet className="w-7 h-7 text-primary" strokeWidth={1.5}/> Wallet
                </h1>
                <p className="text-sm text-muted-foreground mt-2">Every AI activity (drafting, chat assistant, summaries) deducts in real-time.</p>
            </div>

            <section className="bg-card border border-border p-6" data-testid="wallet-balance-card">
                <div className="flex items-end justify-between flex-wrap gap-4">
                    <div>
                        <div className="overline">Current balance</div>
                        <div className={`text-4xl font-light mt-1 ${isEmpty ? "text-rose-400" : isLow ? "text-amber-400" : "text-foreground"}`} data-testid="wallet-balance">
                            ₹{balance.toFixed(2)}
                        </div>
                        <div className="text-[11px] text-muted-foreground mt-1">
                            {data.currency} · low-balance threshold ₹{threshold.toFixed(0)} · min top-up ₹{walletCfg.min_topup}
                        </div>
                    </div>
                    {canTopup ? (
                        <div className="flex flex-col gap-2 items-end" data-testid="wallet-recharge-controls">
                            <div className="flex gap-2 flex-wrap justify-end">
                                {walletCfg.presets.map(p => (
                                    <button key={p} onClick={() => setAmount(p)} data-testid={`recharge-preset-${p}`}
                                        className={`px-3 py-1.5 border text-xs ${amount === p ? "border-primary text-primary bg-primary/10" : "border-border text-muted-foreground hover:text-foreground"}`}>
                                        ₹{p}
                                    </button>
                                ))}
                            </div>
                            <div className="flex gap-2">
                                <input type="number" min={walletCfg.min_topup} max={walletCfg.max_topup || undefined} value={amount} onChange={e => setAmount(parseInt(e.target.value) || 0)} data-testid="recharge-amount-input"
                                    className="bg-background border border-border focus:border-primary outline-none px-3 py-2 text-sm w-28 text-right"/>
                                <button onClick={recharge} disabled={busy} data-testid="wallet-recharge-btn"
                                    className="px-5 py-2 bg-primary text-primary-foreground hover:bg-[#C9A537] disabled:opacity-40 text-xs font-medium uppercase tracking-[0.15em] inline-flex items-center gap-2">
                                    {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin"/> : <Plus className="w-3.5 h-3.5"/>} Recharge
                                </button>
                            </div>
                            {bonusQuote?.eligible && bonusQuote.bonus_amount > 0 && (
                                <div className="mt-1 text-[11px] text-emerald-300 inline-flex items-center gap-1.5"
                                     data-testid="wallet-founder-bonus-hint">
                                    <span className="font-semibold">+₹{bonusQuote.bonus_amount.toLocaleString()} founder bonus</span>
                                    <span className="text-emerald-300/80">— you&apos;ll receive ₹{bonusQuote.total_credited.toLocaleString()} total ({bonusQuote.bonus_pct}% bonus, {bonusQuote.days_remaining} days left)</span>
                                </div>
                            )}
                        </div>
                    ) : (
                        <div className="text-[11px] text-muted-foreground italic text-right max-w-[260px]" data-testid="wallet-recharge-locked">
                            Only the firm owner or managing partner can top-up the wallet. Ask them to recharge if the balance is low.
                        </div>
                    )}
                </div>
                {(isLow || isEmpty) && (
                    <div className={`mt-5 px-3 py-2 border ${isEmpty ? "border-rose-500/40 bg-rose-500/10 text-rose-300" : "border-amber-500/40 bg-amber-500/10 text-amber-300"} text-xs flex items-center gap-2`} data-testid="wallet-warning">
                        <AlertCircle className="w-3.5 h-3.5"/>
                        {isEmpty ? "Wallet is empty. AI features are paused until you recharge." : "Balance is below your low-balance threshold."}
                    </div>
                )}
            </section>

            {/* ---------- Monthly trends (Phase 8) ---------- */}
            <MonthlyTrendsCard trends={data.monthly_trends || []}/>

            {/* ---------- Passbook (filters + table) ---------- */}
            <section className="bg-card border border-border" data-testid="wallet-passbook">
                <header className="px-6 py-4 border-b border-border space-y-3">
                    <div className="flex items-center justify-between flex-wrap gap-2">
                        <div>
                            <h2 className="text-sm uppercase tracking-[0.15em] text-foreground">Passbook</h2>
                            <p className="text-[11px] text-muted-foreground mt-1">{data.transactions.length} transactions · newest first · running balance reflects state after each row.</p>
                        </div>
                        {anyFilter && (
                            <button onClick={clearFilters} data-testid="wallet-clear-filters"
                                className="text-[10px] text-primary hover:text-primary uppercase tracking-[0.16em]">
                                Clear filters
                            </button>
                        )}
                    </div>
                    <div className="flex flex-wrap items-center gap-2 text-xs">
                        <div className="text-[10px] text-muted-foreground uppercase tracking-[0.16em] inline-flex items-center gap-1">
                            <Filter className="w-3 h-3"/> Filters
                        </div>
                        <select value={filters.provider} data-testid="wallet-filter-provider"
                            onChange={e => setFilters({ ...filters, provider: e.target.value })}
                            className="bg-background border border-border px-2 py-1.5 text-xs focus:border-primary outline-none">
                            <option value="">All providers</option>
                            {(data.providers || []).map(p =>
                                <option key={p.id} value={p.id}>{p.label} ({p.count})</option>
                            )}
                        </select>
                        <select value={filters.user_id} data-testid="wallet-filter-user"
                            onChange={e => setFilters({ ...filters, user_id: e.target.value })}
                            className="bg-background border border-border px-2 py-1.5 text-xs focus:border-primary outline-none">
                            <option value="">All users</option>
                            {(data.users || []).map(u =>
                                <option key={u.id} value={u.id}>{u.name || u.email || u.id} ({u.count})</option>
                            )}
                        </select>
                        <select value={filters.month} data-testid="wallet-filter-month"
                            onChange={e => setFilters({ ...filters, month: e.target.value })}
                            className="bg-background border border-border px-2 py-1.5 text-xs focus:border-primary outline-none">
                            <option value="">All months</option>
                            {monthOptions.map(m => <option key={m.value} value={m.value}>{m.label}</option>)}
                        </select>
                    </div>
                </header>
                <FilterChip ctrl={passbookCtrl} className="m-3"/>
                <div className="overflow-x-auto">
                    <table className="w-full text-xs">
                        <thead className="bg-background text-[10px] uppercase tracking-[0.15em] text-muted-foreground">
                            <tr>
                                <SortableTh field="created_at" ctrl={passbookCtrl} className="text-left px-4 py-2">When</SortableTh>
                                <SortableTh field="provider" ctrl={passbookCtrl} className="text-left px-4 py-2">Provider</SortableTh>
                                <SortableTh field="activity" ctrl={passbookCtrl} className="text-left px-4 py-2">Activity</SortableTh>
                                <SortableTh field="by" ctrl={passbookCtrl} className="text-left px-4 py-2">By</SortableTh>
                                <SortableTh field="amount" ctrl={passbookCtrl} align="right" className="text-right px-4 py-2">Amount (₹)</SortableTh>
                                <SortableTh field="running_balance" ctrl={passbookCtrl} align="right" className="text-right px-4 py-2">Running balance (₹)</SortableTh>
                            </tr>
                        </thead>
                        <tbody>
                            {passbookCtrl.rows.map(t => (
                                <tr key={t.id} className="border-t border-border" data-testid={`txn-row-${t.id}`}>
                                    <td className="px-4 py-2 text-muted-foreground">{fmtDateTime(t.created_at)}</td>
                                    <td className="px-4 py-2">
                                        <span className={`text-[10px] uppercase tracking-[0.16em] px-1.5 py-0.5 border ${
                                            t.provider === "ecourts" ? "border-primary/40 text-primary" :
                                            t.provider === "ai"      ? "border-purple-400/40 text-purple-300" :
                                            t.provider === "topup"   ? "border-emerald-400/40 text-emerald-300" :
                                                                       "border-border text-muted-foreground"
                                        }`}>{t.provider_label || t.provider}</span>
                                    </td>
                                    <td className="px-4 py-2 text-foreground max-w-md truncate">{t.note || t.ref}</td>
                                    <td className="px-4 py-2 text-muted-foreground text-[11px]">{t.user_name || t.user_email || "—"}</td>
                                    <td className={`px-4 py-2 text-right ${t.kind === "credit" ? "text-emerald-400" : "text-rose-400"}`}>
                                        <span className="inline-flex items-center gap-1">
                                            {t.kind === "credit" ? <ArrowDownLeft className="w-3 h-3"/> : <ArrowUpRight className="w-3 h-3"/>}
                                            {t.kind === "credit" ? "+" : "−"}{Number(t.amount).toFixed(2)}
                                        </span>
                                    </td>
                                    <td className="px-4 py-2 text-right text-foreground">{t.running_balance.toFixed(2)}</td>
                                </tr>
                            ))}
                            {passbookCtrl.rows.length === 0 && <tr><td colSpan={6} className="text-center text-muted-foreground py-8">No transactions match these filters</td></tr>}
                        </tbody>
                    </table>
                </div>
            </section>
        </div>
    );
}

function MonthlyTrendsCard({ trends }) {
    // Max debit across the 12-month window — used to scale bar heights.
    const max = Math.max(1, ...trends.map(t => t.debits || 0));
    const totalSpend = trends.reduce((s, t) => s + (t.debits || 0), 0);
    const totalTopup = trends.reduce((s, t) => s + (t.credits || 0), 0);

    return (
        <section className="bg-card border border-border p-6" data-testid="wallet-trends">
            <header className="flex items-center justify-between flex-wrap gap-2 mb-4">
                <div>
                    <h2 className="text-sm uppercase tracking-[0.15em] text-foreground inline-flex items-center gap-2">
                        <TrendingUp className="w-4 h-4 text-primary"/> Monthly spend
                    </h2>
                    <p className="text-[11px] text-muted-foreground mt-1">Last 12 months · debits (spend) vs credits (top-ups).</p>
                </div>
                <div className="text-right text-[11px]">
                    <div className="text-rose-300">Spent · ₹{totalSpend.toFixed(2)}</div>
                    <div className="text-emerald-300">Topped-up · ₹{totalTopup.toFixed(2)}</div>
                </div>
            </header>
            <div className="flex items-end gap-2 h-32" data-testid="wallet-trends-bars">
                {trends.map(t => {
                    const pct = max > 0 ? Math.round((t.debits / max) * 100) : 0;
                    const topupPct = max > 0 ? Math.round((t.credits / max) * 100) : 0;
                    return (
                        <div key={t.month} className="flex-1 flex flex-col items-center gap-1 min-w-[36px]"
                             data-testid={`trend-${t.month}`}
                             title={`${t.month}: spent ₹${t.debits.toFixed(2)} · topped-up ₹${t.credits.toFixed(2)} · ${t.count} txns`}>
                            <div className="w-full h-24 flex items-end justify-center gap-0.5">
                                <div className="w-2 bg-rose-400/70 hover:bg-rose-400 transition-colors" style={{ height: `${pct}%` }}/>
                                <div className="w-2 bg-emerald-400/70 hover:bg-emerald-400 transition-colors" style={{ height: `${topupPct}%` }}/>
                            </div>
                            <div className="text-[9px] text-muted-foreground tracking-tight">{t.month.slice(5)}</div>
                        </div>
                    );
                })}
            </div>
        </section>
    );
}
